import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { Text } from '@lightspeed/flame/Text';
import { themeGet } from '@styled-system/theme-get';

import { Decimal } from '~/main/utils/Decimal';
import { ButtonBaseTile } from '~/renderer/components/UI/ButtonBaseTile';
import { InputBaseTile } from '~/renderer/components/UI/InputBaseTile';
import { useMoney } from '~/renderer/hooks/.';
import { ANALYTICS_EVENTS } from '~/renderer/store/middleware/types';
import { receiptActions } from '~/renderer/store/receipt';
import { getTip } from '~/renderer/store/receipt/selectors';
import { track } from '~/renderer/utils/analytics';

import type { Tip } from '~/renderer/store/receipt/types';
import type { AppState } from '~/renderer/store/types';

const customTipId = 'custom-tip';
interface StyledFlippingInputInnerProps {
  focused: boolean;
}

const StyledFlippingInput = styled('div')`
  width: 100%;
  height: 72px;
  perspective: 1000px;
`;

const StyledFlippingInputInner = styled('div')<StyledFlippingInputInnerProps>`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-content: center;
  align-items: center;
  transition-property: transform;
  transition-duration: ${themeGet('transition.transition-duration-base')};
  transform-style: preserve-3d;
  transform: ${({ focused }) => (focused ? 'rotateY(180deg)' : 'rotateY(0deg)')};

  & > * {
    position: absolute;
    width: 100%;
    height: 100%;
    -webkit-backface-visibility: hidden; /* Safari */
    backface-visibility: hidden;
  }
`;

const StyledCustomTipInput = styled(InputBaseTile)`
  transform: rotateY(-180deg);
`;

interface Props {
  maxLength: number;
}

const CustomTipInput: React.FC<Props> = ({ maxLength }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { money, parseMoney } = useMoney();
  const tip = useSelector<AppState, Tip | null>(getTip);
  const customInputRef = React.useRef<HTMLInputElement>(
    null,
  ) as React.MutableRefObject<HTMLInputElement>;
  const [amount, setAmount] = React.useState<Decimal>(new Decimal(0));
  const [inputAmount, setInputAmount] = React.useState<string>(money(0));
  const [isFocus, setIsFocus] = React.useState<boolean>(false);

  React.useEffect(() => {
    // We reset everything if the tipId is not the custom-tip
    if (tip?.enteredTip?.tipId !== customTipId) {
      setInputAmount('');
      setAmount(new Decimal(0));
    }
  }, [tip]);

  // Handlers - Amount Input
  const updateEnteredTip = (tipId = '', enteredTipAmount: number | null) => {
    dispatch(
      receiptActions.updateEnteredTip({
        tipId,
        enteredTipAmount: enteredTipAmount ?? null,
      }),
    );
  };

  const handleOnChangeAmount = (event: React.ChangeEvent<HTMLInputElement>) => {
    const sanitizedInputValue = event.target.value.replace(/[^\d.]/, '');
    const allowInputPattern = new RegExp(/^\d{0,11}(.?\d{0,2})?$/);

    if (sanitizedInputValue.match(allowInputPattern) || sanitizedInputValue === '') {
      setInputAmount(sanitizedInputValue);
    }
  };

  const handleOnFocusAmount = () => {
    setIsFocus(true);
    // We reset the tip value set so the cardholder don't have to use backspace
    updateEnteredTip(customTipId, null);
    setInputAmount('');
  };

  const handleOnBlurAmount = async (): Promise<void> => {
    const parsedAmount = new Decimal(parseMoney(inputAmount));
    setIsFocus(false);
    setAmount(parsedAmount);
    updateEnteredTip(customTipId, parsedAmount?.toNumber());

    await track(ANALYTICS_EVENTS.TIP_OPTION, {
      tip_input: 'custom',
    });
  };

  const handleOnKeyPressAmount = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // prevent the global keypress handler from firing
    event.stopPropagation();
    if (event.key === 'Enter') {
      // blurring the input will trigger the handleAmountBlur method
      event.currentTarget.blur();
    }
  };

  return (
    <StyledFlippingInput>
      <StyledFlippingInputInner focused={tip?.enteredTip?.tipId === customTipId}>
        <ButtonBaseTile
          aria-label="custom-tip-button"
          fullWidth={true}
          onClick={() => {
            customInputRef.current.focus();
          }}
        >
          <Text size="xlarge">{t('receipt.tipping.customTip')}</Text>
        </ButtonBaseTile>
        <StyledCustomTipInput
          aria-label="custom-tip-input"
          fullWidth={true}
          value={isFocus ? inputAmount : money(amount?.toNumber() || 0)}
          onChange={handleOnChangeAmount}
          onFocus={handleOnFocusAmount}
          onBlur={handleOnBlurAmount}
          onKeyPress={handleOnKeyPressAmount}
          autoComplete="none"
          maxLength={maxLength}
          pattern="[0-9]*"
          ref={customInputRef}
        />
      </StyledFlippingInputInner>
    </StyledFlippingInput>
  );
};

export { CustomTipInput };
